import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Download, FileText, BookOpen, Lightbulb } from 'lucide-react';

const resources = [
  {
    title: 'Website Launch Checklist',
    description: 'A comprehensive checklist to ensure your website launch goes smoothly.',
    type: 'Checklist',
    icon: FileText
  },
  {
    title: 'SEO Starter Guide',
    description: 'Learn the fundamentals of SEO and how to improve your search rankings.',
    type: 'Guide',
    icon: BookOpen
  },
  {
    title: 'Social Media Calendar Template',
    description: 'Plan and organize your social media content with this free template.',
    type: 'Template',
    icon: FileText
  },
  {
    title: 'E-Commerce Best Practices',
    description: 'Tips and strategies for building a successful online store.',
    type: 'Guide',
    icon: Lightbulb
  },
  {
    title: 'Brand Guidelines Template',
    description: 'Create consistent branding with this customizable template.',
    type: 'Template',
    icon: FileText
  },
  {
    title: 'Website Performance Audit',
    description: 'Learn how to audit and improve your website\'s performance.',
    type: 'Guide',
    icon: BookOpen
  }
];

export default function FreeResourcesPage() {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-muted/50 to-background">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge variant="secondary" className="mb-6">
              <Download className="h-4 w-4 mr-2" />
              Free Resources
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-6">
              Free{' '}
              <span className="gradient-text">Resources</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Guides, templates, and tools to help you succeed in the digital world.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {resources.map((resource, index) => {
              const Icon = resource.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="p-6 h-full border-border hover:border-primary/30 transition-all duration-300 card-hover flex flex-col">
                    <div className="w-12 h-12 rounded-lg gradient-primary-soft flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <Badge variant="outline" className="w-fit mb-3">
                      {resource.type}
                    </Badge>
                    <h3 className="text-xl font-display font-semibold text-foreground mb-2">
                      {resource.title}
                    </h3>
                    <p className="text-muted-foreground mb-4 flex-grow">
                      {resource.description}
                    </p>
                    <Button variant="outline" className="w-full mt-auto">
                      <Download className="h-4 w-4 mr-2" />
                      Download Free
                    </Button>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 gradient-primary">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-6">
              Need Personalized Help?
            </h2>
            <p className="text-primary-foreground/80 mb-8">
              Our team is ready to help you with your specific needs.
            </p>
            <Button variant="premium" size="lg" asChild className="bg-background text-foreground hover:bg-background/90">
              <Link to="/contact">
                Contact Us
                <ArrowRight className="h-5 w-5 ml-2" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
